import { createContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import "react-toastify/dist/ReactToastify.css";
import { setIsLoggingOut } from "../utils/AuthFlags";
import { takePostLoginRedirect } from "../utils/postLoginRedirect";
import { API_BASE_URL_WITH_SLASH } from "../config/apiConfig";

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {
  const toast = useToast();
  const BaseURL = API_BASE_URL_WITH_SLASH;
  const [isSupplier, setIsSupplier] = useState(false);
  const [authTokens, setAuthTokens] = useState(() =>
    localStorage.getItem("authTokens")
      ? JSON.parse(localStorage.getItem("authTokens"))
      : null
  );

  const [user, setUser] = useState(() =>
    localStorage.getItem("authTokens")
      ? jwtDecode(JSON.parse(localStorage.getItem("authTokens")).access)
      : null
  );

  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const loginUser = async (email, password, next = "/") => {
    try {
      const response = await fetch(`${BaseURL}token/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

       if (!response.ok) throw new Error(data.detail || "Login failed");

      setAuthTokens(data);
      const decodedToken = jwtDecode(data.access);
      setUser(decodedToken);
      setIsSupplier(decodedToken.is_supplier);
      localStorage.setItem("authTokens", JSON.stringify(data));

      // ✅ Check for returnUrl in sessionStorage
      const returnUrl = sessionStorage.getItem('returnUrl');
      // Where an invitation was taking a new user before they signed up (see Signup.jsx).
      const invitedTo = takePostLoginRedirect();
      if (returnUrl) {
        sessionStorage.removeItem('returnUrl'); // Clean up
        navigate(returnUrl);
      } else if (next && next !== "/") {
        navigate(next);
      } else {
        navigate(invitedTo || next);
      }
    } catch (error) {
      toast({
        title: "Login Error",
        description: error.message || "Email or Password is incorrect.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const registerUser = async (email, username, password, password2) => {
    try {
      // Optional: validate passwords match before sending
      if (password !== password2) {
        toast({
          title: "Password Mismatch",
          description: "Passwords do not match.",
          status: "warning",
          duration: 4000,
          isClosable: true,
          position: "bottom",
        });
        return;
      }

      const response = await fetch(`${BaseURL}register/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          username,
          password,
          password2,
        }),
      });

      const data = await response.json();

      if (response.status === 201) {
        toast({
          title: "Success",
          description: "Account was registered successfully.",
          status: "success",
          duration: 4000,
          isClosable: true,
          position: "bottom",
        });
        navigate("/login");
      } else {
        toast({
          title: "Error",
          description: data.detail || "An error occurred during registration.",
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "bottom",
        });
      }
    } catch (error) {
      toast({
        title: "Network Error",
        description: error.message || "Something went wrong.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const logoutUser = async (silent = false) => {
    setIsLoggingOut(false);

    // ── Tell the backend to blacklist the refresh token ──────────────────
    const tokens = authTokens || JSON.parse(localStorage.getItem("authTokens"));
    if (tokens?.refresh) {
      try {
        await fetch(`${BaseURL}token/logout/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokens.access}`,
          },
          body: JSON.stringify({ refresh: tokens.refresh }),
        });
      } catch {
        // Network failure — still clear client state below
      }
    }
    // ─────────────────────────────────────────────────────────────────────

    setAuthTokens(null);
    setUser(null);
    localStorage.removeItem("authTokens");

    toast({
      title: silent ? "Session expired" : "Logged out",
      description: silent ? "Please log in again." : "You have been successfully logged out.",
      status: silent ? "warning" : "info",
      duration: 3000,
      isClosable: true,
      position: "bottom",
    });

    navigate("/login");
  };


  const contextData = {
    user,
    isSupplier,
    setIsSupplier,
    setUser,
    authTokens,
    setAuthTokens,
    registerUser,
    loginUser,
    logoutUser,
  };

  useEffect(() => {
    const checkTokenValidity = () => {
      if (authTokens) {
        const decodedToken = jwtDecode(authTokens.access);

        const now = Date.now() / 1000; // current time in seconds
        if (decodedToken.exp < now) {
          // Token has expired
          logoutUser(true); // pass a flag for silent/expired logout
        } else {
          setUser(decodedToken);
          setIsSupplier(decodedToken.is_supplier);
        }
      }
      setLoading(false);
    };

    checkTokenValidity();
  }, []);

  return (
    <AuthContext.Provider value={contextData}>
      {loading ? null : children}
    </AuthContext.Provider>
  );
};
 